import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageHeader from '../../components/common/PageHeader';
import Badge from '../../components/common/Badge';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { db, storage } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { friendlyDate, toLocalDateStr } from '../../utils/dateFormat';
const TEACHER_FILTERS = ['전체', '진행중', '마감', '미채점'];
const STUDENT_FILTERS = ['전체', '미제출', '제출완료', '채점완료'];

const AssignmentPage = () => {
  const { currentUser, userData } = useAuth();
  const { canEdit, canGrade, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const [activeFilter, setActiveFilter] = useState(0);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [formData, setFormData] = useState({});
  const [gradingItem, setGradingItem] = useState(null);
  const [gradeInputs, setGradeInputs] = useState({});

  // 학생 제출 모달
  const [submitItem, setSubmitItem] = useState(null);
  const [submitText, setSubmitText] = useState('');
  const [submitFile, setSubmitFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!userData?.uid) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const loadData = async () => {
    try {
      const uid = userData.uid;
      const lookupUid = isParent ? (userData.children?.[0] || uid) : uid;
      const classQ = isChildView
        ? query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid))
        : query(collection(db, 'classes'), where('teacherId', '==', uid));
      const classSnap = await getDocs(classQ);
      const classList = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setClasses(classList);

      const allAssignments = [];
      for (const cls of classList) {
        const aSnap = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        aSnap.docs.forEach(d => {
          allAssignments.push({
            id: d.id,
            classId: cls.id,
            className: cls.name,
            classColor: cls.color,
            studentIds: cls.studentIds || [],
            ...d.data(),
          });
        });
      }
      allAssignments.sort((a, b) => (b.dueDate || '').localeCompare(a.dueDate || ''));
      setAssignments(allAssignments);
    } catch (err) {
      console.error('과제 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (item) => {
    const today = toLocalDateStr(new Date());
    const submissions = item.submissions || {};
    const hasUngraded = Object.values(submissions).some(s => s.submitted && (s.score === undefined || s.score === null));
    if (hasUngraded) return 'ungraded';
    if (item.dueDate && item.dueDate < today) return 'closed';
    return 'active';
  };

  // 학생/학부모: 제출 상태
  const targetUid = isParent ? (userData?.children?.[0] || userData?.uid) : userData?.uid;
  const getMyStatus = (item) => {
    const sub = (item.submissions || {})[targetUid];
    if (!sub || !sub.submitted) return 'not_submitted';
    if (sub.score !== undefined && sub.score !== null) return 'graded';
    return 'submitted';
  };

  const FILTERS = isChildView ? STUDENT_FILTERS : TEACHER_FILTERS;

  const filtered = assignments.filter(item => {
    if (activeFilter === 0) return true;
    if (isChildView) {
      const myStatus = getMyStatus(item);
      if (activeFilter === 1) return myStatus === 'not_submitted';
      if (activeFilter === 2) return myStatus === 'submitted';
      if (activeFilter === 3) return myStatus === 'graded';
    } else {
      const status = getStatus(item);
      if (activeFilter === 1) return status === 'active';
      if (activeFilter === 2) return status === 'closed';
      if (activeFilter === 3) return status === 'ungraded';
    }
    return true;
  });

  const handleCreateAssignment = async () => {
    if (!selectedClassId || !formData.title || !formData.dueDate) return;
    await addDoc(collection(db, 'classes', selectedClassId, 'assignments'), {
      title: formData.title,
      description: formData.description || '',
      dueDate: formData.dueDate,
      maxScore: Number(formData.maxScore) || 100,
      submissions: {},
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
    });
    setShowCreateModal(false);
    setFormData({});
    loadData();
  };

  const handleGradeSubmit = async () => {
    if (!gradingItem) return;
    const updatedSubmissions = { ...gradingItem.submissions };
    Object.entries(gradeInputs).forEach(([sid, score]) => {
      if (score !== '' && score !== undefined) {
        updatedSubmissions[sid] = {
          ...(updatedSubmissions[sid] || {}),
          score: Number(score),
          gradedBy: currentUser.uid,
          gradedAt: new Date().toISOString(),
        };
      }
    });
    await updateDoc(doc(db, 'classes', gradingItem.classId, 'assignments', gradingItem.id), {
      submissions: updatedSubmissions,
    });
    setGradingItem(null);
    setGradeInputs({});
    loadData();
  };

  return (
    <Container>
      <PageHeader
        title={isChildView ? (isParent ? '자녀 과제' : '내 과제') : '수업과제 관리'}
        rightAction={canEdit ? <AddBtn onClick={() => setShowCreateModal(true)}>+ 수업과제 등록</AddBtn> : null}
      />

      <FilterRow>
        {FILTERS.map((f, i) => (
          <FilterBtn key={f} $active={activeFilter === i} onClick={() => setActiveFilter(i)}>
            {f}
          </FilterBtn>
        ))}
      </FilterRow>

      <Content>
        {loading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <EmptyText>과제가 없습니다.</EmptyText>
        ) : (
          filtered.map(item => {
            const submissions = item.submissions || {};
            const submittedCount = Object.values(submissions).filter(s => s.submitted).length;
            const ungradedCount = Object.values(submissions).filter(s => s.submitted && (s.score === undefined || s.score === null)).length;
            const status = getStatus(item);

            if (isChildView) {
              // 학생/학부모 뷰
              const mySub = submissions[targetUid];
              const myStatus = getMyStatus(item);
              return (
                <Card key={`${item.classId}-${item.id}`} $mb="12px" $padding="16px">
                  <AssignmentTop>
                    <div>
                      <Badge $variant="subject">{item.className}</Badge>
                      <Badge
                        $variant={myStatus === 'graded' ? 'completed' : myStatus === 'submitted' ? 'inProgress' : 'notSubmitted'}
                        style={{ marginLeft: 6 }}
                      >
                        {myStatus === 'graded' ? '채점완료' : myStatus === 'submitted' ? '제출완료' : '미제출'}
                      </Badge>
                    </div>
                  </AssignmentTop>
                  <AssignmentTitle>{item.title}</AssignmentTitle>
                  {item.description && <AssignmentDesc>{item.description}</AssignmentDesc>}
                  <AssignmentMeta>마감: {friendlyDate(item.dueDate)}</AssignmentMeta>
                  {myStatus === 'graded' && (
                    <SubmissionRow>
                      <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>점수: {mySub.score} / {item.maxScore || 100}</span>
                    </SubmissionRow>
                  )}
                  {myStatus === 'submitted' && (
                    <SubmissionRow>
                      <span style={{ color: '#22C55E' }}>제출 내용: {mySub.text || '(파일 제출)'}</span>
                      {mySub.fileUrl && (
                        <a href={mySub.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--color-primary)', marginLeft: 8 }}>
                          {mySub.fileName || '첨부파일'}
                        </a>
                      )}
                    </SubmissionRow>
                  )}
                  {myStatus === 'not_submitted' && isStudent && (
                    <GradeButton onClick={() => { setSubmitItem(item); setSubmitText(''); setSubmitFile(null); }}>
                      과제 제출하기
                    </GradeButton>
                  )}
                </Card>
              );
            }

            // 강사 뷰
            return (
              <Card key={`${item.classId}-${item.id}`} $mb="12px" $padding="16px">
                <AssignmentTop>
                  <div>
                    <Badge $variant="subject">{item.className}</Badge>
                    <Badge $variant={status === 'closed' ? 'urgent' : status === 'ungraded' ? 'notSubmitted' : 'inProgress'} style={{ marginLeft: 6 }}>
                      {status === 'closed' ? '마감' : status === 'ungraded' ? '미채점' : '진행중'}
                    </Badge>
                  </div>
                </AssignmentTop>
                <AssignmentTitle>{item.title}</AssignmentTitle>
                {item.description && <AssignmentDesc>{item.description}</AssignmentDesc>}
                <AssignmentMeta>
                  마감: {friendlyDate(item.dueDate)}
                </AssignmentMeta>
                <SubmissionRow>
                  <span>제출: {submittedCount}/{item.studentIds.length}명</span>
                  <span>미제출: {item.studentIds.length - submittedCount}명</span>
                  {ungradedCount > 0 && <Badge $variant="urgent">미채점 {ungradedCount}</Badge>}
                </SubmissionRow>
                {canGrade && ungradedCount > 0 && (
                  <GradeButton onClick={() => {
                    setGradingItem(item);
                    const inputs = {};
                    Object.entries(submissions).forEach(([sid, sub]) => {
                      if (sub.submitted) inputs[sid] = sub.score ?? '';
                    });
                    setGradeInputs(inputs);
                  }}>
                    채점하기
                  </GradeButton>
                )}
              </Card>
            );
          })
        )}
      </Content>

      {/* 과제 출제 모달 */}
      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>수업과제 등록</ModalTitle>
              <CloseBtn onClick={() => setShowCreateModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>클래스 선택 *</FieldLabel>
              <FieldSelect value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)}>
                <option value="">선택하세요</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </FieldSelect>
              <FieldLabel>제목 *</FieldLabel>
              <FieldInput
                placeholder=""
                value={formData.title || ''}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
              />
              <FieldLabel>설명</FieldLabel>
              <FieldTextarea
                placeholder=""
                value={formData.description || ''}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              />
              <FieldLabel>마감일 *</FieldLabel>
              <FieldInput
                type="date"
                value={formData.dueDate || ''}
                onChange={e => setFormData(p => ({ ...p, dueDate: e.target.value }))}
              />
              <ModalButtons>
                <CancelBtn onClick={() => setShowCreateModal(false)}>취소</CancelBtn>
                <SubmitBtn onClick={handleCreateAssignment}>출제</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}

      {/* 학생 과제 제출 모달 */}
      {submitItem && (
        <ModalOverlay onClick={() => setSubmitItem(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{submitItem.title} 제출</ModalTitle>
              <CloseBtn onClick={() => setSubmitItem(null)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>과제 내용</FieldLabel>
              <FieldTextarea
                placeholder=""
                value={submitText}
                onChange={e => setSubmitText(e.target.value)}
              />
              <FieldLabel>파일 첨부</FieldLabel>
              <FileInputWrap>
                <FileInput
                  type="file"
                  id="assignSubmitFile"
                  onChange={e => setSubmitFile(e.target.files[0] || null)}
                />
                <FileInputLabel htmlFor="assignSubmitFile">
                  {submitFile ? submitFile.name : '파일을 선택하세요'}
                </FileInputLabel>
              </FileInputWrap>
              <ModalButtons>
                <CancelBtn onClick={() => setSubmitItem(null)}>취소</CancelBtn>
                <SubmitBtn disabled={submitting} onClick={async () => {
                  if (!submitText.trim() && !submitFile) return;
                  setSubmitting(true);
                  try {
                    let fileUrl = null;
                    let fileName = null;
                    if (submitFile) {
                      const storageRef = ref(storage, `assignments/${submitItem.classId}/${submitItem.id}/${userData.uid}_${Date.now()}_${submitFile.name}`);
                      await uploadBytes(storageRef, submitFile);
                      fileUrl = await getDownloadURL(storageRef);
                      fileName = submitFile.name;
                    }
                    const updatedSubmissions = { ...(submitItem.submissions || {}) };
                    updatedSubmissions[userData.uid] = {
                      submitted: true,
                      text: submitText.trim(),
                      submittedAt: new Date().toISOString(),
                      ...(fileUrl && { fileUrl, fileName }),
                    };
                    await updateDoc(doc(db, 'classes', submitItem.classId, 'assignments', submitItem.id), {
                      submissions: updatedSubmissions,
                    });
                    setSubmitItem(null);
                    setSubmitText('');
                    setSubmitFile(null);
                    loadData();
                  } catch (err) {
                    console.error('제출 실패:', err);
                  } finally {
                    setSubmitting(false);
                  }
                }}>{submitting ? '제출 중...' : '제출'}</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}

      {/* 채점 모달 */}
      {gradingItem && (
        <ModalOverlay onClick={() => setGradingItem(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{gradingItem.title} 채점</ModalTitle>
              <CloseBtn onClick={() => setGradingItem(null)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              {Object.entries(gradingItem.submissions || {})
                .filter(([, sub]) => sub.submitted)
                .map(([sid, sub]) => (
                  <div key={sid}>
                    <GradeRow>
                      <span>{sid}</span>
                      <GradeInput
                        type="number"
                        min="0"
                        max={gradingItem.maxScore || 100}
                        value={gradeInputs[sid] ?? ''}
                        onChange={e => setGradeInputs(p => ({ ...p, [sid]: e.target.value }))}
                        placeholder=""
                      />
                      <span>/ {gradingItem.maxScore || 100}</span>
                    </GradeRow>
                    {(sub.text || sub.fileUrl) && (
                      <div style={{ padding: '6px 0 12px', fontSize: 13, color: '#6B7280' }}>
                        {sub.text && <div style={{ marginBottom: 4 }}>내용: {sub.text}</div>}
                        {sub.fileUrl && (
                          <a href={sub.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                            첨부: {sub.fileName || '파일 보기'}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              <ModalButtons>
                <CancelBtn onClick={() => setGradingItem(null)}>취소</CancelBtn>
                <SubmitBtn onClick={handleGradeSubmit}>저장</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AssignmentPage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const AddBtn = styled.button`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid rgba(255,255,255,0.5);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const FilterRow = styled.div`
  display: flex;
  padding: 4px 16px 0;
  gap: 24px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
  &::-webkit-scrollbar { display: none; }
`;

const FilterBtn = styled.button`
  padding: 12px 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ $active }) => $active ? 700 : 400};
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text.hint};
  background: none;
  white-space: nowrap;
  flex-shrink: 0;
  margin-bottom: -1px;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const AssignmentTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const AssignmentTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  margin-bottom: 6px;
`;

const AssignmentDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
  margin-bottom: 8px;
`;

const AssignmentMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 8px;
`;

const SubmissionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GradeButton = styled.button`
  width: 100%;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 10px;
`;

const GradeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  span:first-child { flex: 1; }
`;

const GradeInput = styled.input`
  width: 80px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 8px;
  text-align: center;
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 6px;
  margin-top: 14px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  min-height: 80px;
  resize: vertical;
`;

const FileInputWrap = styled.div`
  position: relative;
  margin-top: 4px;
`;

const FileInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
`;

const FileInputLabel = styled.label`
  display: block;
  padding: 12px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
  text-align: center;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.background.default}; }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SubmitBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
`;
